import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiCheck } from 'react-icons/fi';
import { Card, Badge, Button, Input, Spinner, PageSkeleton } from '@daycal/ui';
import { api } from '../../lib/apiClient.js';

// Super Admin plan management (architecture.md §24 M10). Plans are DATA and render on the
// public landing + upgrade pages (shared ['plans'] query key), so edits here update pricing
// everywhere instantly. Full editor: name, price, badge, features, capability flags, etc.

const CAPS = [
  ['allow_paid_bookings', 'Paid bookings'],
  ['allow_api_access', 'REST API access'],
  ['allow_mobile_app', 'Mobile app'],
  ['allow_custom_domains', 'Custom domains'],
  ['allow_teams', 'Team booking'],
  ['allow_event_redirect', 'Redirect URL'],
];

const EMPTY = {
  name: '', price_display: '', billing_cycle: '', badge_tag: '', button_text: '',
  display_order: 0, description: '', features_text: '', max_event_types: 1, max_monthly_bookings: -1, max_employee_seats: 0,
  allow_google_calendar_sync: false, allow_paid_bookings: false, allow_api_access: false,
  allow_mobile_app: false, allow_custom_domains: false, allow_teams: false,
  allow_event_redirect: false, is_featured_dark: false,
};

// DB row -> form state (features_list[] <-> newline-separated textarea).
function toForm(plan) {
  if (!plan) return { ...EMPTY };
  return {
    ...EMPTY,
    ...plan,
    billing_cycle: plan.billing_cycle || '',
    badge_tag: plan.badge_tag || '',
    button_text: plan.button_text || '',
    description: plan.description || '',
    features_text: (plan.features_list || []).join('\n'),
  };
}

// Form state -> API payload (matches planSchema / updatePlanSchema).
function toPayload(f) {
  return {
    name: f.name.trim(),
    price_display: f.price_display.trim(),
    billing_cycle: f.billing_cycle.trim() || null,
    badge_tag: f.badge_tag.trim() || null,
    button_text: f.button_text.trim() || null,
    display_order: Number(f.display_order) || 0,
    description: f.description.trim() || null,
    features_list: f.features_text.split('\n').map((s) => s.trim()).filter(Boolean),
    max_event_types: Number(f.max_event_types),
    max_monthly_bookings: Number(f.max_monthly_bookings),
    max_employee_seats: f.allow_teams ? Number(f.max_employee_seats) : 0,
    allow_google_calendar_sync: !!f.allow_google_calendar_sync,
    allow_paid_bookings: !!f.allow_paid_bookings,
    allow_api_access: !!f.allow_api_access,
    allow_mobile_app: !!f.allow_mobile_app,
    allow_custom_domains: !!f.allow_custom_domains,
    allow_teams: !!f.allow_teams,
    allow_event_redirect: !!f.allow_event_redirect,
    is_featured_dark: !!f.is_featured_dark,
  };
}

const labelCls = 'mb-1 block text-sm font-medium text-slate-700';
const fieldCls =
  'w-full rounded-dc-sm border border-slate-400 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500';

function PlanModal({ plan, onClose }) {
  const qc = useQueryClient();
  const isNew = !plan?.id;
  const [form, setForm] = useState(() => toForm(plan));
  const [error, setError] = useState('');
  const set = (key) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const save = useMutation({
    mutationFn: () => (isNew ? api.adminCreatePlan(toPayload(form)) : api.adminUpdatePlan(plan.id, toPayload(form))),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'plans'] });
      qc.invalidateQueries({ queryKey: ['plans'] });
      onClose();
    },
    onError: (err) => setError(err.details?.map((d) => `${d.path}: ${d.message}`).join(', ') || err.message),
  });

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4" onClick={onClose}>
      <div
        className="my-8 w-full max-w-2xl rounded-dc bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-5 text-lg font-semibold">{isNew ? 'New plan' : `Edit ${plan.name}`}</h2>

        <form
          className="grid gap-4"
          onSubmit={(e) => { e.preventDefault(); setError(''); save.mutate(); }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Name" value={form.name} onChange={set('name')} required />
            <Input label="Price display" value={form.price_display} onChange={set('price_display')} placeholder="₹749 or Custom" required />
            <Input label="Billing cycle" value={form.billing_cycle} onChange={set('billing_cycle')} placeholder="per month / forever" />
            <Input label="Badge tag" value={form.badge_tag} onChange={set('badge_tag')} placeholder="Popular / Most Popular" />
            <Input label="Button text" value={form.button_text} onChange={set('button_text')} placeholder="Upgrade" />
            <Input label="Display order" type="number" value={form.display_order} onChange={set('display_order')} />
            <label className="block">
              <span className={labelCls}>Max event types</span>
              <input className={fieldCls} type="number" min={-1} value={form.max_event_types} onChange={set('max_event_types')} />
              <span className="mt-1 block text-xs text-slate-400">-1 = unlimited</span>
            </label>
            <label className="block">
              <span className={labelCls}>Max bookings / month</span>
              <input className={fieldCls} type="number" min={-1} value={form.max_monthly_bookings} onChange={set('max_monthly_bookings')} />
              <span className="mt-1 block text-xs text-slate-400">-1 = unlimited · resets monthly</span>
            </label>
          </div>

          <label className="block">
            <span className={labelCls}>Description</span>
            <textarea className={fieldCls} rows={2} value={form.description} onChange={set('description')} />
          </label>

          <label className="block">
            <span className={labelCls}>Features (one per line)</span>
            <textarea className={`${fieldCls} font-mono`} rows={6} value={form.features_text} onChange={set('features_text')} placeholder={'Includes all Free features\nBuilt-in video calls'} />
          </label>

          <div>
            <span className={labelCls}>Capabilities</span>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {CAPS.map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={form[key]} onChange={set(key)} /> {label}
                </label>
              ))}
            </div>
          </div>

          {form.allow_teams && (
            <label className="block sm:max-w-xs">
              <span className={labelCls}>Max team members</span>
              <input className={fieldCls} type="number" min={-1} value={form.max_employee_seats} onChange={set('max_employee_seats')} />
              <span className="mt-1 block text-xs text-slate-400">Employee seat cap · -1 = unlimited</span>
            </label>
          )}

          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={form.is_featured_dark} onChange={set('is_featured_dark')} />
            Featured (dark card)
          </label>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="mt-2 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save plan'}</Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default function AdminPlans() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'plans'], queryFn: api.adminListPlans });
  const [editing, setEditing] = useState(null); // plan object, or {} for new, or null (closed)
  const [confirming, setConfirming] = useState(null); // plan id pending delete confirmation
  const [delError, setDelError] = useState('');

  // Any plan change affects both the admin list (incl. inactive) and the public pricing list.
  const invalidatePlans = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'plans'] });
    qc.invalidateQueries({ queryKey: ['plans'] });
  };

  const toggleFeatured = useMutation({
    mutationFn: ({ id, value }) => api.adminUpdatePlan(id, { is_featured_dark: value }),
    onSuccess: invalidatePlans,
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, value }) => api.adminUpdatePlan(id, { active: value }),
    onSuccess: invalidatePlans,
  });

  const del = useMutation({
    mutationFn: (id) => api.adminDeletePlan(id),
    onSuccess: () => {
      setConfirming(null);
      setDelError('');
      invalidatePlans();
    },
    onError: (err) => setDelError(err.details?.map((d) => `${d.path}: ${d.message}`).join(', ') || err.message),
  });

  if (isLoading) return <PageSkeleton />;
  const plans = data?.plans || [];

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Plans</h1>
        <Button onClick={() => setEditing({})}>+ New plan</Button>
      </div>

      {delError && (
        <div className="mb-4 rounded-dc-sm border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {delError}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {plans.map((p) => (
          <Card key={p.id} featured={p.is_featured_dark} className={p.active === false ? 'opacity-60' : ''}>
            {p.active === false && (
              <span className="mb-2 inline-block rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">Inactive</span>
            )}
            {p.badge_tag && <Badge tone="brand" className="mb-2">{p.badge_tag}</Badge>}
            <h3 className="text-lg font-semibold">{p.name}</h3>
            <div className="my-1 text-2xl font-bold">{p.price_display}</div>
            <div className={`text-xs ${p.is_featured_dark ? 'text-slate-300' : 'text-slate-400'}`}>
              {p.billing_cycle || '—'} · max event types: {p.max_event_types === -1 ? '∞' : p.max_event_types}
              {' · '}bookings/mo: {p.max_monthly_bookings === -1 ? '∞' : p.max_monthly_bookings}
              {' · '}team: {p.allow_teams ? (p.max_employee_seats === -1 ? '∞' : p.max_employee_seats) : 'off'}
            </div>

            <p className={`mt-3 text-sm leading-relaxed ${p.is_featured_dark ? 'text-slate-300' : 'text-slate-500'}`}>
              {p.description}
            </p>

            <div className={`my-4 h-px ${p.is_featured_dark ? 'bg-slate-700' : 'bg-slate-100'}`} />
            
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Features:</p>
            <ul className="mt-3 flex-1 space-y-2 mb-4">
              {(p.features_list || []).map((feat, i) => (
                <li key={i} className={`flex items-start gap-2.5 text-sm ${p.is_featured_dark ? 'text-slate-200' : 'text-slate-600'}`}>
                  <FiCheck className={`mt-0.5 h-4 w-4 shrink-0 ${p.is_featured_dark ? 'text-brand-500' : 'text-emerald-500'}`} />
                  <span>{feat}</span>
                </li>
              ))}
            </ul>

            <div className="mt-3 grid gap-2">
              <Button className="w-full" onClick={() => setEditing(p)}>Edit</Button>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => toggleFeatured.mutate({ id: p.id, value: !p.is_featured_dark })}
              >
                {p.is_featured_dark ? 'Unfeature' : 'Make featured'}
              </Button>
              <Button
                variant="secondary"
                className="w-full"
                disabled={toggleActive.isPending}
                onClick={() => toggleActive.mutate({ id: p.id, value: p.active === false })}
              >
                {p.active === false ? 'Activate' : 'Deactivate'}
              </Button>
              {confirming === p.id ? (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => { setConfirming(null); setDelError(''); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    className="w-full"
                    disabled={del.isPending}
                    onClick={() => del.mutate(p.id)}
                  >
                    {del.isPending ? 'Deleting…' : 'Confirm'}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="danger"
                  className="w-full"
                  onClick={() => { setConfirming(p.id); setDelError(''); }}
                >
                  Delete
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {editing && <PlanModal plan={editing.id ? editing : null} onClose={() => setEditing(null)} />}
    </div>
  );
}
